<template>
    <div>
        <div
            class="upload-zone border-2 border-dashed rounded-3 p-4 text-center position-relative"
            :class="{ 'border-primary bg-primary bg-opacity-10': isDragging, 'border-secondary': !isDragging }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop">
            <i class="fas fa-cloud-upload-alt fa-3x text-primary mb-3 d-block"></i>
            <div class="mb-3">
                <label for="fileInput" class="btn btn-primary btn-lg px-4">
                    <i class="fas fa-folder-open me-2"></i> ファイルを選択
                    <input
                        id="fileInput"
                        ref="fileInput"
                        type="file"
                        :multiple="multiple"
                        accept="image/*,application/pdf,.pdf"
                        :disabled="uploading"
                        @change="onFileChange"
                        class="d-none"
                    />
                </label>
            </div>
            <p class="text-muted mb-0">
                または、ここにファイルをドラッグ&ドロップ
                <span class="d-block small mt-1">
                    画像・PDF対応（{{ multiple ? '合計' : '最大' }}{{ maxFilesize }}MBまで{{ multiple ? '、複数選択可' : '' }}）
                </span>
            </p>
        </div>

        <!-- 選択中ファイルのリスト -->
        <div v-if="files.length" class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0 fw-bold">
                    <i class="fas fa-file me-2 text-info"></i>選択中のファイル（画像・PDF）
                </h6>
                <button class="btn btn-success" :disabled="uploading" @click="upload">
                    <i class="fas fa-upload me-2"></i>アップロード開始
                </button>
            </div>

            <div class="list-group">
                <div
                    v-for="(f, i) in files"
                    :key="i"
                    class="list-group-item d-flex justify-content-between align-items-center"
                >
                    <div class="d-flex align-items-center flex-grow-1">
                        <i :class="getFileIconClass(f)"></i>
                        <span class="text-truncate">{{ f.name }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        <span class="badge bg-secondary rounded-pill">{{ formatSize(f.size) }}</span>
                        <button
                            v-if="multiple"
                            class="btn btn-sm btn-outline-danger"
                            :disabled="uploading"
                            @click="removeFile(i)"
                        >
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="alert alert-info d-flex justify-content-between align-items-center mt-2 mb-0">
                <span>
                    <i class="fas fa-info-circle me-2"></i>
                    {{ multiple ? '合計サイズ' : 'ファイルサイズ' }}: {{ formatSize(totalSize) }}
                </span>
                <span class="badge bg-info">上限 {{ maxFilesize }}MB</span>
            </div>
        </div>

        <!-- アップロード中のプログレス -->
        <div v-if="uploading" class="mt-3">
            <div class="progress" style="height: 25px;">
                <div
                    class="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style="width: 100%"
                >
                    <i class="fas fa-spinner fa-spin me-2"></i>アップロード中...
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .upload-zone {
        transition: all 0.3s ease;
    }

    .upload-zone:hover {
        background-color: rgba(var(--bs-primary-rgb), 0.05) !important;
    }
</style>

<script>
export default {
    props: {
        url: {
            type: String,
            required: true
        },
        multiple: {
            type: Boolean,
            default: true
        },
        maxFilesize: {
            type: Number,
            default: 10
        },
        fieldName: {
            type: String,
            default: 'images[]'
        },
        id: {
            default: null,
        },
    },
    data() {
        return {
            files: [],
            uploading: false,
            isDragging: false,
        }
    },
    computed: {
        totalSize() {
            return this.files.reduce((sum, f) => sum + f.size, 0)
        }
    },
    methods: {
        /**
         * 画像またはPDFのみ許可する判定。
         * MIMEが空の環境を考慮して拡張子もフォールバックで確認する。
         * @param {File} file
         * @returns {boolean}
         */
        isSupportedFile(file) {
            const file_name = (file.name || '').toLowerCase()
            const is_image = file.type.startsWith('image/')
            const is_pdf = file.type === 'application/pdf' || file_name.endsWith('.pdf')
            return is_image || is_pdf
        },
        filterSupportedFiles(file_list) {
            const valid_files = file_list.filter(file => this.isSupportedFile(file))
            if (valid_files.length !== file_list.length) {
                alert('画像またはPDFファイルのみアップロードできます。')
            }
            return valid_files
        },
        getFileIconClass(file) {
            const file_name = (file.name || '').toLowerCase()
            const is_pdf = file.type === 'application/pdf' || file_name.endsWith('.pdf')
            return is_pdf ? 'fas fa-file-pdf me-2 text-danger' : 'fas fa-file-image me-2 text-muted'
        },
        onDrop(e) {
            this.isDragging = false

            const dropped_files = Array.from(e.dataTransfer.files)
            const valid_files = this.filterSupportedFiles(dropped_files)

            if (!this.multiple && valid_files.length > 0) {
                this.files = [valid_files[0]]
            } else {
                this.files = valid_files
            }

            if (!this.validateSize()) {
                this.files = []
            }
        },
        formatSize(bytes) {
            if (bytes === 0) return '0B'
            const units = ['B', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(1024))
            const value = bytes / Math.pow(1024, i)
            return `${value.toFixed(1)}${units[i]}`
        },
        onFileChange(e) {
            const selected_files = Array.from(e.target.files)
            const valid_files = this.filterSupportedFiles(selected_files)

            if (!this.multiple && valid_files.length > 0) {
                this.files = [valid_files[0]]
            } else {
                this.files = valid_files
            }

            if (!this.validateSize()) {
                e.target.value = ''
                this.files = []
            }
        },
        validateSize() {
            const totalSize = this.files.reduce((sum, f) => sum + f.size, 0)
            const maxBytes = this.maxFilesize * 1024 * 1024

            if (totalSize > maxBytes) {
                alert(`ファイルの${this.multiple ? '合計' : ''}サイズは${this.maxFilesize}MBまでです。`)
                return false
            }
            return true
        },
        removeFile(index) {
            this.files.splice(index, 1)
        },
        upload() {
            if (!this.files.length) return;

            this.uploading = true;

            const formData = new FormData();

            if (this.multiple) {
                this.files.forEach(f => formData.append(this.fieldName, f));
            } else {
                const fieldName = this.fieldName.replace('[]', '');
                formData.append(fieldName, this.files[0]);
            }
            if (this.id) formData.append('id', this.id);

            axios.post(this.url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(() => {
                this.files = [];
                const input = this.$refs.fileInput;
                if (input) input.value = '';
                this.$emit('uploaded');
            }).catch(e => {
                if (e.response && e.response.status === 422) {
                    Alert.err((e.response.data && e.response.data.message) || 'アップロード失敗（バリデーションエラー）');
                } else {
                    Alert.err('アップロード失敗');
                }
            }).finally(() => {
                this.uploading = false;
            });
        },
    }
}
</script>
